import { FeatureId } from 'terra-draw/dist/store/store';
import * as turf from '@turf/turf';
import {
  TerraDraw,
  TerraDrawRectangleMode,
  TerraDrawFreehandMode,
  TerraDrawSelectMode,
  TerraDrawPointMode,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
  ValidateMinAreaSquareMeters,
} from 'terra-draw';
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter';
import * as maplibregl from 'maplibre-gl';

export enum TerraDrawMode_Enum {
  CIRCLE = 'circle',
  FREEHAND = 'freehand',
  LINESTRING = 'linestring',
  POINT = 'point',
  POLYGON = 'polygon',
  RECTANGLE = 'rectangle',
  ANGLED_RECTANGLE = 'angled-rectangle',
  SECTOR = 'sector',

  SELECT = 'select',
}

export const TerraDrawUtil = {
  DrawLayerZIndex: 1000,
  LabelLayerZIndex: 1001,

  /**
   * getFeatureById
   * @param id
   * @returns
   */
  getFeatureById: (id: string) => {
    const snapshot = window.draw?.getSnapshot();
    if (snapshot) {
      return snapshot.find((feature) => feature.id === id);
    }
    return null;
  },
  removeFeature: (id: string) => {
    window.draw?.removeFeatures([id]);
  },

  /**
   * setup terra draw, construct window.draw: TerraDraw instance
   */
  setup: () => {
    const pointerDistance = 10;
    window.draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({
        map: window.map!,
        coordinatePrecision: 12,
      }),
      modes: [
        new TerraDrawSelectMode({
          pointerDistance: pointerDistance,
          projection: 'web-mercator',
          flags: {
            arbitrary: {
              feature: {},
            },
            polygon: {
              feature: {
                draggable: false,
                rotateable: true,
                scaleable: true,
                coordinates: {
                  midpoints: true,
                  draggable: true,
                  deletable: true,
                },
              },
            },
            freehand: {
              feature: { draggable: true, coordinates: {} },
            },
            linestring: {
              feature: {
                draggable: false,
                rotateable: true,
                scaleable: true,
                coordinates: {
                  midpoints: true,
                  draggable: true,
                  deletable: true,
                },
              },
            },
            rectangle: {
              feature: {
                draggable: true,
                coordinates: {
                  midpoints: false,
                  draggable: true,
                  resizable: 'center',
                  deletable: true,
                },
              },
            },
            circle: {
              feature: {
                validation: (feature) => {
                  if (feature.geometry.type !== 'Polygon') {
                    return false;
                  }

                  return ValidateMinAreaSquareMeters(feature, 1000);
                },
                draggable: true,
                coordinates: {
                  midpoints: false,
                  draggable: true,
                  resizable: 'center-fixed',
                  deletable: true,
                },
              },
            },
            point: {
              feature: {
                draggable: true,
              },
            },
          },
        }),
        new TerraDrawPointMode({
          pointerDistance: pointerDistance,
        }),
        new TerraDrawLineStringMode({
          pointerDistance: pointerDistance,
          // // Pass styles to the constructor
          // styles: {
          //   // Fill colour (a string containing a 6 digit Hex color)
          //   lineStringColor: "#FF0000",
          // },
        }),
        new TerraDrawPolygonMode({
          pointerDistance: pointerDistance,
        }),
      ],
    });
  },

  stopDrawing: () => {
    if (window.draw?.enabled) {
      window.draw?.clear();
      window.draw?.stop();
    }
    TerraDrawUtil.clearLabels();
  },

  /**
   * TerraDraw have no option to display labels for the drawn features.
   * This function will display the length of the line segments and area of the polygon.
   */
  showLabels: () => {
    // TODO: implement show labels
    // console.log('TerraDrawUtil setup');
    // {
    //   const vectorLayer = new VectorLayer({
    //     source: vectorSource,
    //     style: (feature) =>
    //       new Style({
    //         text: new Text({
    //           text: feature.get('length') || '-', // Text to display (can be dynamic)
    //           offsetY: -15, // Move the text above the circle
    //           fill: new Fill({ color: '#000' }), // Text color
    //           stroke: new Stroke({ color: '#fff', width: 2 }), // Outline for better readability
    //           font: '14px sans-serif', // Font style
    //         }),
    //         // image: new Circle({
    //         //   radius: 5,
    //         //   fill: new Fill({ color: 'red' }),
    //         //   stroke: new Stroke({ color: 'black', width: 2 })
    //         // }),
    //       }),
    //   });
    //   vectorLayer.setZIndex(TerraDrawUtil.LabelLayerZIndex);
    //   window.map?.addLayer(vectorLayer);
    // }
    // function getMidpoint(coordinates: any) {
    //   const a = coordinates[0]; // [x1, y1]
    //   const b = coordinates[1]; // [x2, y2]
    //   return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    // }
    // function addLengthLabel(coordinate: any, text: string) {
    //   const pointFeature = new Feature(new Point(coordinate));
    //   pointFeature.getGeometry()?.transform('EPSG:4326', 'EPSG:3857');
    //   pointFeature.setProperties({ length: text });
    //   // Add the label feature to the vector source
    //   vectorSource.addFeature(pointFeature);
    // }
    // function displaySegmentLength(coordinates: any) {
    //   const linestringFeature = new Feature(new LineString(coordinates));
    //   linestringFeature;
    //   const geoJson = new GeoJSON().writeFeatureObject(linestringFeature);
    //   // console.log("geoJson", geoJson);
    //   const length = turf.length(geoJson, { units: 'meters' });
    //   // Find midpoint of the line to display the label
    //   const midpoint = getMidpoint(coordinates);
    //   // console.log("midpoint", midpoint);
    //   // Add text style to the midpoint
    //   if (length > 1000.0) {
    //     addLengthLabel(midpoint, `${(length / 1000.0).toFixed(2)} km`);
    //   } else if (length > 1.0) {
    //     addLengthLabel(midpoint, `${length.toFixed(2)} m`);
    //   } else {
    //     // too small to display
    //   }
    // }
    // function displayArea(coordinates: Coordinate[][]) {
    //   const geoJson = new GeoJSON().writeFeatureObject(new Feature(new Polygon(coordinates)));
    //   // console.log("geoJson", geoJson);
    //   const area = turf.area(geoJson);
    //   const centroid = turf.centerOfMass(geoJson).geometry.coordinates;
    //   // console.log("centroid", centroid);
    //   // Add text style to the centroid
    //   if (area > 1000000) {
    //     addLengthLabel(centroid, `${(area / 1000000.0).toFixed(2)} km²`);
    //   } else {
    //     addLengthLabel(centroid, `${area.toFixed(2)} m²`);
    //   }
    // }
    // let lasttime = 0;
    // window.draw?.on('change', (ids: FeatureId[], type: string) => {
    //   const now = new Date().getTime();
    //   if (now - lasttime < 50) {
    //     return;
    //   }
    //   lasttime = now;
    //   if (type === 'update') {
    //     // update, delete
    //     vectorSource.clear();
    //     const drawLabelsOnFeature = (id: FeatureId) => {
    //       const feature = TerraDrawUtil.getFeatureById(id as string);
    //       if (feature && ['linestring', 'polygon'].includes(feature.geometry.type.toLowerCase())) {
    //         // loop through geometry segments, display ol.Text for each segment
    //         let lineString: number[][] = [];
    //         {
    //           if (feature.geometry.type === 'LineString') {
    //             lineString = feature.geometry.coordinates;
    //           }
    //           if (feature.geometry.type === 'Polygon') {
    //             lineString = feature.geometry.coordinates[0];
    //           }
    //         }
    //         for (let i = 1; i < lineString.length; i++) {
    //           const segment = [lineString[i - 1], lineString[i]];
    //           // console.log("segment", segment);
    //           displaySegmentLength(segment);
    //         }
    //       }
    //       if (feature && 'polygon' == feature.geometry.type.toLowerCase()) {
    //         // console.log("feature.geometry.coordinates", feature.geometry.coordinates);
    //         displayArea(feature.geometry.coordinates as Coordinate[][]);
    //       }
    //     };
    //     window.draw?.getSnapshot().forEach((feature) => {
    //       const id = feature.id as FeatureId;
    //       drawLabelsOnFeature(id);
    //     });
    //     // ids.forEach((id) => {
    //     //     drawLabelsOnFeature(id);
    //     // });
    //   }
    // });
  },
  clearLabels: () => {
    // TODO: implement clearLabels
    // vectorSource.clear();
  },

  getFormatedLength: (value: number) => {
    if (value > 1000) {
      return `${(value / 1000).toFixed(2)} km`;
    } else {
      return `${(value / 1.0).toFixed(2)} m`;
    }
  },
  getFormatedArea: (value: number) => {
    if (value > 1000000) {
      return `${(value / 1000000).toFixed(2)} km2`;
    } else {
      return `${(value / 1.0).toFixed(2)} m2`;
    }
  },
};
